/* Weekly Progress Digest — global Bornwood roadmap movement for the project owner.
   Reads the guide movement history (assignments, status flips, comments) and turns
   the last 7 days into a command summary: headline stats, status balance, per-teammate
   throughput, the recent movement feed, and the stale-task radar. Platform admin only
   (GET /guide/digest is enforced server-side). */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, CheckCircle2, MessageSquare, AlertTriangle, RefreshCcw, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const card = "border border-white/[0.08] bg-[#0A0A0C] p-6 flex flex-col gap-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]";
const sub = "font-mono text-[9px] font-bold tracking-[0.25em] text-zinc-500 uppercase";

const STATUS_CLS = {
  done: "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10",
  doing: "text-amber-300 border-amber-300/40 bg-amber-300/10",
  blocked: "text-red-400 border-red-400/40 bg-red-400/10",
  todo: "text-zinc-400 border-white/15 bg-white/5",
};

const ago = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export function DigestPanel() {
  const [digest, setDigest] = useState(null);

  const load = useCallback(() => {
    api.get("/guide/digest").then((r) => setDigest(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  useEffect(load, [load]);

  if (!digest) {
    return <div className={card} data-testid="digest-panel"><p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 animate-pulse">Compiling the week…</p></div>;
  }

  const maxMoves = Math.max(1, ...digest.per_person.map((p) => p.moves));
  const statusTotal = Math.max(1, Object.values(digest.by_status).reduce((a, b) => a + b, 0));

  return (
    <div className={card} data-testid="digest-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Weekly Progress Digest
        </p>
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-600">global roadmap · last {digest.window_days} days</span>
        <button data-testid="digest-refresh" onClick={load}
          className="ml-auto font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400 border border-white/10 px-3 py-2 hover:border-nc-orange/50 hover:text-nc-orange transition-colors flex items-center gap-1.5">
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* headline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          [CheckCircle2, digest.done_this_week, "tasks done", "text-[#00E676]", "done"],
          [Activity, digest.moves_total, "task moves", "text-nc-blue", "moves"],
          [MessageSquare, digest.comments_this_week, "comments", "text-zinc-300", "comments"],
          [AlertTriangle, digest.stale.length, "stale tasks", digest.stale.length ? "text-red-400" : "text-zinc-500", "stale"],
        ].map(([Icon, v, label, cls, id]) => (
          <div key={id} data-testid={`digest-stat-${id}`} className="border border-white/[0.08] bg-white/[0.02] px-4 py-3">
            <p className={`font-sans font-bold text-2xl ${cls}`}>{v}</p>
            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mt-1 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col gap-5">
          {/* status balance */}
          <div>
            <p className={sub}>Board balance</p>
            <div className="mt-2 flex h-2 w-full overflow-hidden border border-white/[0.08]">
              {["done", "doing", "blocked", "todo"].map((s) => (
                <span key={s} style={{ width: `${(digest.by_status[s] || 0) / statusTotal * 100}%` }}
                  className={s === "done" ? "bg-[#00E676]" : s === "doing" ? "bg-amber-300" : s === "blocked" ? "bg-red-400" : "bg-zinc-700"} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {["done", "doing", "blocked", "todo"].map((s) => (
                <span key={s} data-testid={`digest-status-${s}`} className={`font-mono text-[8px] uppercase tracking-[0.15em] border px-2 py-1 ${STATUS_CLS[s]}`}>
                  {s} · {digest.by_status[s] || 0}
                </span>
              ))}
            </div>
          </div>

          {/* per-teammate throughput */}
          <div>
            <p className={sub}>Throughput per teammate</p>
            {digest.per_person.length === 0 && <p className="font-mono text-[10px] text-zinc-400 mt-2">No movement logged this week — the board has been quiet.</p>}
            <div className="mt-2 flex flex-col gap-2">
              {digest.per_person.map((p) => (
                <div key={p.name} data-testid={`digest-person-${p.name.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3">
                  <span className="font-mono text-[10px] text-zinc-200 w-28 truncate">{p.name}</span>
                  <div className="flex-1 h-1.5 bg-white/[0.04]">
                    <div className="h-full bg-nc-orange/70" style={{ width: `${p.moves / maxMoves * 100}%` }} />
                  </div>
                  <span className="font-mono text-[9px] text-zinc-500 w-20 text-right">{p.moves} moves · {p.done} done</span>
                </div>
              ))}
            </div>
          </div>

          {/* stale radar */}
          <div>
            <p className={sub}>Stale radar — untouched 14+ days</p>
            {digest.stale.length === 0 && <p className="font-mono text-[10px] text-[#00E676] mt-2">Nothing stale. The board is alive.</p>}
            <div className="mt-2 flex flex-col gap-1.5">
              {digest.stale.map((t) => (
                <div key={t.task_id} data-testid={`digest-stale-${t.task_id.toLowerCase()}`} className="flex items-center gap-2.5 border border-white/[0.06] bg-white/[0.02] px-3 py-2 flex-wrap">
                  <span className="font-mono text-[10px] font-bold text-zinc-100">{t.task_id}</span>
                  <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[t.status] || STATUS_CLS.todo}`}>{t.status}</span>
                  <span className="font-mono text-[9px] text-zinc-500">{t.assignee || "unassigned"}</span>
                  <span className="ml-auto font-mono text-[8px] text-red-400/80 uppercase">{ago(t.updated_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* movement feed */}
        <div>
          <p className={sub}>Movement feed</p>
          {digest.moves.length === 0 && <p className="font-mono text-[10px] text-zinc-400 mt-2">No moves recorded in the window yet.</p>}
          <div className="mt-2 flex flex-col gap-1.5 max-h-[420px] overflow-y-auto pr-1">
            {digest.moves.map((m) => (
              <div key={m.id} data-testid="digest-move-row" className="flex items-center gap-2.5 border-b border-white/[0.05] pb-1.5 flex-wrap">
                <span className="font-mono text-[10px] font-bold text-nc-blue">{m.task_id}</span>
                <span className="font-mono text-[9px] text-zinc-400">{m.actor}</span>
                {m.kind === "status" ? (
                  <span className="font-mono text-[9px] text-zinc-500 flex items-center gap-1">
                    <span className={m.from === "done" ? "text-[#00E676]" : "text-zinc-500"}>{m.from || "todo"}</span>
                    <ArrowRight className="w-3 h-3 text-zinc-600" />
                    <span className={m.to === "done" ? "text-[#00E676]" : m.to === "blocked" ? "text-red-400" : "text-amber-300"}>{m.to}</span>
                  </span>
                ) : (
                  <span className="font-mono text-[9px] text-zinc-500">assigned → <span className="text-zinc-200">{m.to}</span></span>
                )}
                <span className="ml-auto font-mono text-[8px] text-zinc-600 uppercase">{ago(m.at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="font-mono text-[9px] text-zinc-400">Source: the live guide board at /new-citizen/bridge-guide — every assignment, status flip and comment is logged automatically.</p>
    </div>
  );
}
